import React, { useEffect, useState } from "react";
import { X, MapPin, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Owner-facing modal that shows a rep's last known location on OpenStreetMap.
 * Uses the tile service (no API key required) via an iframe embed.
 */
export default function RepLocationModal({ repId, repName, onClose, L }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const fetchLoc = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/rep/${repId}/location`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || (L ? "Αποτυχία φόρτωσης" : "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLoc(); /* eslint-disable-line */ }, [repId]);

  const hasCoords = data && data.lat != null && data.lng != null;
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(L ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  // Build OSM embed bbox around the point (~0.01 deg = ~1 km)
  const mapUrl = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${data.lng - 0.008}%2C${data.lat - 0.006}%2C${data.lng + 0.008}%2C${data.lat + 0.006}&layer=mapnik&marker=${data.lat}%2C${data.lng}`
    : null;
  const externalUrl = hasCoords
    ? `https://www.openstreetmap.org/?mlat=${data.lat}&mlon=${data.lng}#map=15/${data.lat}/${data.lng}`
    : null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-2xl w-full border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="rep-location-modal"
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              {L ? "Γεωεντοπισμός πωλητή" : "Rep location"}
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-0.5">{repName}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="rep-location-refresh"
              onClick={fetchLoc}
              disabled={loading}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500 disabled:opacity-40"
              title={L ? "Ανανέωση" : "Refresh"}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100 text-slate-500">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Status pills */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {data?.is_online ? (
              <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-semibold" data-testid="rep-location-online">
                <Wifi className="w-3 h-3" /> {L ? "Online" : "Online"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-semibold" data-testid="rep-location-offline">
                <WifiOff className="w-3 h-3" /> {L ? "Offline" : "Offline"}
              </span>
            )}
            {data?.updated_at && (
              <span className="text-slate-500">
                {L ? "Τελευταία θέση:" : "Last position:"} <span className="font-medium text-slate-700">{fmtTime(data.updated_at)}</span>
              </span>
            )}
            {data?.accuracy != null && (
              <span className="text-slate-500">· ±{Math.round(data.accuracy)}m</span>
            )}
          </div>

          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">{err}</div>}

          {loading && !data && (
            <div className="py-16 text-center text-slate-500 text-sm">{L ? "Φόρτωση..." : "Loading..."}</div>
          )}

          {!loading && !hasCoords && (
            <div className="py-16 text-center space-y-3" data-testid="rep-location-empty">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
                <MapPin className="w-6 h-6" />
              </div>
              <div className="text-sm font-medium text-slate-700">
                {L ? "Δεν υπάρχει καταγεγραμμένη θέση" : "No location on record"}
              </div>
              <div className="text-xs text-slate-500 max-w-sm mx-auto">
                {L
                  ? "Ο πωλητής πρέπει να είναι συνδεδεμένος στην πλατφόρμα και να έχει επιτρέψει τη γεωθέση στον browser του."
                  : "The rep must be logged into the platform and have granted browser location permission."}
              </div>
            </div>
          )}

          {hasCoords && (
            <>
              <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid="rep-location-map">
                <iframe
                  title="rep-location-map"
                  src={mapUrl}
                  style={{ width: "100%", height: "340px", border: 0 }}
                  loading="lazy"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{L ? "Γεωγρ. πλάτος" : "Latitude"}</div>
                  <div className="font-mono font-bold text-slate-900 mt-0.5" data-testid="rep-location-lat">{data.lat.toFixed(6)}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{L ? "Γεωγρ. μήκος" : "Longitude"}</div>
                  <div className="font-mono font-bold text-slate-900 mt-0.5" data-testid="rep-location-lng">{data.lng.toFixed(6)}</div>
                </div>
              </div>

              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rep-location-external"
                className="block text-center text-sm text-[#0052CC] hover:underline font-semibold"
              >
                {L ? "Άνοιγμα σε OpenStreetMap →" : "Open in OpenStreetMap →"}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
